import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppButton } from "components/button/AppButton";
import {
  getNextSection,
  getPreviousSection,
  isLastSection,
  isFirstSection,
} from "../../services/profileNavigationService";

interface ProfileNavigationButtonsProps {
  onSave?: () => void;
  onNext?: () => void;
  isLoading?: boolean;
  isSubmitting?: boolean;
  hasForm?: boolean;
  nextButtonLabel?: string;
  previousButtonLabel?: string;
  disabled?: boolean;
}

export const ProfileNavigationButtons: React.FC<
  ProfileNavigationButtonsProps
> = ({
  onSave,
  onNext,
  isLoading = false,
  isSubmitting = false,
  hasForm = true,
  nextButtonLabel = "Next",
  previousButtonLabel = "← Previous",
  disabled = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const nextSection = getNextSection(location.pathname);
  const prevSection = getPreviousSection(location.pathname);
  const lastSection = isLastSection(location.pathname);
  const firstSection = isFirstSection(location.pathname);

  const handleNext = () => {
    if (disabled) return;

    if (hasForm && onSave) {
      onSave();
    } else if (!hasForm && onNext) {
      onNext();
    } else if (nextSection) {
      navigate(nextSection.link);
    }
  };

  const handleComplete = () => {
    if (disabled) return;

    if (onNext) {
      onNext();
    } else if (onSave) {
      onSave();
    }
  };

  const handlePrevious = () => {
    if (prevSection) {
      navigate(prevSection.link);
    }
  };

  return (
    <div className="flex justify-between items-center mt-6 pt-4 border-t">
      {!firstSection && (
        <button
          type="button"
          onClick={handlePrevious}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {previousButtonLabel}
        </button>
      )}

      <div className="flex gap-3 ml-auto">
        {!lastSection && (
          <AppButton
            type="button"
            label={nextButtonLabel}
            handleClick={handleNext}
            isLoading={isLoading || isSubmitting}
            disabled={disabled}
          />
        )}

        {lastSection && (
          <AppButton
            type="button"
            label="Complete Profile"
            handleClick={handleComplete}
            isLoading={isLoading || isSubmitting}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};
